import React, { useState, useEffect, useCallback } from 'react';
import { HelpCircle, BarChart2, BookOpen, Search, Filter, Trash2, Loader2, Plus, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const BANDS = ['Primary 1-3', 'Primary 4-6', 'JSS 1-3', 'SS 1-3'];

function QuestionModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    question_text: '', class_level: 'JSS 1-3', subject: 'Mathematics',
    options: ['', '', '', ''], correct_index: 0,
    explanation: '', tip: '', difficulty: 'medium',
    question_type: 'mcq', exercise_context: '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.question_text.trim()) { toast.error('Question text required'); return; }
    if (form.options.some(o => !o.trim())) { toast.error('All 4 options required'); return; }
    setSaving(true);
    try {
      const res = await api.post('/quiz/master/questions', form);
      if (res?.success) { toast.success('Question added'); onSaved(); }
    } catch { toast.error('Failed to add question'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-slate-800">Add Question</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400 hover:text-slate-600" /></button>
        </div>
        <div className="space-y-3">
          <textarea value={form.question_text} onChange={e => setForm(f => ({ ...f, question_text: e.target.value }))}
            rows={3} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-200 outline-none resize-none"
            placeholder="Question text *" data-testid="q-text-input" />

          {form.question_type === 'exercise' && (
            <textarea value={form.exercise_context || ''} onChange={e => setForm(f => ({ ...f, exercise_context: e.target.value }))}
              rows={2} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-200 outline-none resize-none"
              placeholder="Reading passage / context (optional)" />
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Class Level</label>
              <select value={form.class_level} onChange={e => setForm(f => ({ ...f, class_level: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none">
                {BANDS.map(b => <option key={b} value={b}>{b}</option>)}
                {['Primary 1','Primary 2','Primary 3','Primary 4','Primary 5','Primary 6','JSS 1','JSS 2','JSS 3','SS 1','SS 2','SS 3'].map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Type</label>
              <select value={form.question_type} onChange={e => setForm(f => ({ ...f, question_type: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none">
                <option value="mcq">MCQ</option>
                <option value="exercise">Exercise (Passage-based)</option>
                <option value="image">Image-based</option>
                <option value="graph">Graph/Data</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 block">Options (select correct answer)</label>
            {form.options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <button onClick={() => setForm(f => ({ ...f, correct_index: i }))}
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${form.correct_index === i ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                  {form.correct_index === i && <Check className="w-3 h-3 text-white" />}
                </button>
                <input value={opt} onChange={e => { const o = [...form.options]; o[i] = e.target.value; setForm(f => ({ ...f, options: o })); }}
                  className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 outline-none"
                  placeholder={`Option ${String.fromCharCode(65 + i)}`} />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Tip</label>
              <input value={form.tip || ''} onChange={e => setForm(f => ({ ...f, tip: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Study tip..." />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Difficulty</label>
              <select value={form.difficulty} onChange={e => setForm(f => ({ ...f, difficulty: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none">
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Explanation</label>
            <textarea value={form.explanation || ''} onChange={e => setForm(f => ({ ...f, explanation: e.target.value }))}
              rows={2} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-200 outline-none resize-none"
              placeholder="Explanation shown after answer..." />
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-[#2563eb] hover:bg-[#1d58d4] text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="save-question-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add Question
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MasterQuizManagementPage() {
  const [stats, setStats] = useState({});
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterBand, setFilterBand] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleting, setDeleting] = useState('');
  const PER_PAGE = 30;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PER_PAGE, skip: page * PER_PAGE });
      if (filterBand) params.set('class_level', filterBand);
      if (filterSubject) params.set('subject', filterSubject);
      if (search) params.set('q', search);
      const [qRes, sRes] = await Promise.allSettled([
        api.get(`/quiz/master/questions?${params}`),
        api.get('/quiz/master/stats'),
      ]);
      if (qRes.status === 'fulfilled' && qRes.value?.success) {
        setQuestions(qRes.value.data || []);
        setTotal(qRes.value.total || 0);
      }
      if (sRes.status === 'fulfilled' && sRes.value?.success) setStats(sRes.value.data || {});
    } catch {}
    setLoading(false);
  }, [page, filterBand, filterSubject, search]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    setDeleting(id);
    try {
      const res = await api.delete(`/quiz/master/questions/${id}`);
      if (res?.success) { toast.success('Deleted'); load(); }
    } catch { toast.error('Failed to delete'); }
    setDeleting('');
  };

  const bandCounts = stats.by_class_level || {};

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2" data-testid="master-quiz-title">
            <HelpCircle className="w-7 h-7 text-pink-500" /> Quiz Management
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Manage quiz questions across all class levels — {total.toLocaleString()} total questions</p>
        </div>
        <button onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d58d4] text-white font-bold px-5 py-2.5 rounded-xl shadow-sm transition-colors"
          data-testid="add-question-btn">
          <Plus className="w-4 h-4" /> Add Question
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {BANDS.map(b => (
          <div key={b} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm text-center">
            <p className="text-2xl font-black text-[#2563eb]">{(bandCounts[b] || 0).toLocaleString()}</p>
            <p className="text-xs text-slate-500 mt-0.5">{b}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-200"
            placeholder="Search questions..." data-testid="quiz-search-input" />
        </div>
        <select value={filterBand} onChange={e => { setFilterBand(e.target.value); setPage(0); }}
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none bg-white">
          <option value="">All Levels</option>
          {BANDS.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={filterSubject} onChange={e => { setFilterSubject(e.target.value); setPage(0); }}
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none bg-white">
          <option value="">All Subjects</option>
          {['Mathematics','English Language','Basic Science','Social Studies','Agricultural Science','Economics','Government','Biology','Chemistry','Physics'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center justify-between">
          <span>Questions {total > 0 && `(${page * PER_PAGE + 1}–${Math.min((page + 1) * PER_PAGE, total)} of ${total})`}</span>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-7 h-7 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : questions.length === 0 ? (
          <div className="py-16 text-center">
            <HelpCircle className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400">No questions match your filters</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {questions.map(q => (
              <div key={q.id} className="flex items-start gap-3 px-5 py-3.5 hover:bg-slate-50" data-testid={`q-row-${q.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700 line-clamp-2">{q.question_text}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">{q.class_level}</span>
                    <span className="text-xs text-slate-400">{q.subject}</span>
                    <span className="text-xs text-slate-400">{q.difficulty}</span>
                    {q.question_type && q.question_type !== 'mcq' && (
                      <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full font-medium">{q.question_type}</span>
                    )}
                  </div>
                </div>
                <button onClick={() => del(q.id)} disabled={deleting === q.id}
                  className="text-slate-400 hover:text-red-500 p-2 rounded-xl hover:bg-red-50 flex-shrink-0 transition-colors">
                  {deleting === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
        )}
        {/* Pagination */}
        {total > PER_PAGE && (
          <div className="px-5 py-3 border-t border-slate-50 flex items-center justify-between">
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
              className="text-xs font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-30">← Prev</button>
            <span className="text-xs text-slate-400">Page {page + 1} of {Math.ceil(total / PER_PAGE)}</span>
            <button disabled={(page + 1) * PER_PAGE >= total} onClick={() => setPage(p => p + 1)}
              className="text-xs font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-30">Next →</button>
          </div>
        )}
      </div>

      {showAddModal && (
        <QuestionModal onClose={() => setShowAddModal(false)} onSaved={() => { setShowAddModal(false); load(); }} />
      )}
    </div>
  );
}
